import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BarChart3, Users, Eye, Monitor, Smartphone, Tablet, 
  TrendingUp, Clock, FileText, Activity, RefreshCw 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AnalyticsDashboard - Real-time and historical analytics for admin panel
 */
export const AnalyticsDashboard = () => {
  const [realtime, setRealtime] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Fetch dashboard data
  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/analytics/dashboard?days=${period}`);
      setDashboard(res.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  };

  // Fetch realtime data
  const fetchRealtime = async () => {
    try {
      const res = await axios.get(`${API}/analytics/realtime`);
      setRealtime(res.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch realtime:', error);
    }
  };

  // Initial load
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchRealtime()]);
      setLoading(false);
    };
    loadAll();
  }, [period]);

  // Realtime refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchRealtime, 30000);
    return () => clearInterval(interval);
  }, []);

  // Device icon helper
  const getDeviceIcon = (device) => {
    switch (device?.toLowerCase()) {
      case 'mobile': return <Smartphone size={16} />;
      case 'tablet': return <Tablet size={16} />;
      default: return <Monitor size={16} />;
    }
  };

  // Calculate bar width for charts
  const getBarWidth = (value, max) => {
    return `${Math.max((value / max) * 100, 5)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin text-[var(--primary)]">
          <RefreshCw size={32} />
        </div>
      </div>
    );
  }

  const maxDailyViews = dashboard?.views_by_day?.length 
    ? Math.max(...dashboard.views_by_day.map(d => d.views)) 
    : 1;

  const maxPageViews = dashboard?.top_pages?.length 
    ? Math.max(...dashboard.top_pages.map(p => p.views)) 
    : 1;

  return (
    <div className="space-y-8">
      {/* Header with Period Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-['Playfair_Display'] text-2xl text-white">Dashboard de Analíticas</h2>
          <p className="text-white/50 text-sm mt-1">
            Última actualización: {lastUpdate.toLocaleTimeString('es-ES')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="bg-white/5 border border-white/10 text-white px-4 py-2 text-sm"
          >
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={90}>Últimos 90 días</option>
          </select>
          <button
            onClick={() => { fetchDashboard(); fetchRealtime(); }}
            className="p-2 bg-white/5 border border-white/10 text-white/70 hover:text-white hover:border-[var(--primary)] transition-all"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Realtime Section */}
      <div className="bg-gradient-to-r from-[var(--primary)]/10 to-transparent border border-[var(--primary)]/30 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity size={20} className="text-green-400 animate-pulse" />
          <h3 className="text-white font-medium">EN VIVO</h3>
          <span className="text-xs text-white/40">(últimos 5 minutos)</span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
              <Users size={24} className="text-green-400" />
            </div>
            <div>
              <p className="text-3xl font-bold text-white">{realtime?.active_users || 0}</p>
              <p className="text-white/50 text-sm">Usuarios activos</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
              <Eye size={24} className="text-blue-400" />
            </div>
            <div>
              <p className="text-3xl font-bold text-white">{realtime?.views_last_5min || 0}</p>
              <p className="text-white/50 text-sm">Vistas en tiempo real</p>
            </div>
          </div>
          
          <div>
            <p className="text-white/50 text-xs uppercase mb-2">Páginas activas ahora</p>
            <div className="space-y-1">
              {realtime?.current_pages?.slice(0, 3).map((page, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-white/70 truncate max-w-[200px]">{page.page}</span>
                  <span className="text-[var(--primary)] font-medium">{page.views}</span>
                </div>
              )) || <p className="text-white/30 text-sm">Sin actividad</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Main Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white/5 border border-white/10 p-5">
          <div className="flex items-center gap-2 text-white/50 text-sm mb-2">
            <Eye size={16} />
            <span>Visitas Totales</span>
          </div>
          <p className="text-3xl font-bold text-white">{dashboard?.metrics?.total_views?.toLocaleString() || 0}</p>
        </div>
        
        <div className="bg-white/5 border border-white/10 p-5">
          <div className="flex items-center gap-2 text-white/50 text-sm mb-2">
            <Users size={16} />
            <span>Sesiones Únicas</span>
          </div>
          <p className="text-3xl font-bold text-white">{dashboard?.metrics?.unique_sessions?.toLocaleString() || 0}</p>
        </div>
        
        <div className="bg-white/5 border border-white/10 p-5">
          <div className="flex items-center gap-2 text-white/50 text-sm mb-2">
            <FileText size={16} />
            <span>Páginas/Sesión</span>
          </div>
          <p className="text-3xl font-bold text-white">{dashboard?.metrics?.pages_per_session || 0}</p>
        </div>
        
        <div className="bg-white/5 border border-white/10 p-5">
          <div className="flex items-center gap-2 text-white/50 text-sm mb-2">
            <TrendingUp size={16} />
            <span>Promedio Diario</span>
          </div>
          <p className="text-3xl font-bold text-white">{dashboard?.metrics?.daily_average || 0}</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Views by Day Chart */}
        <div className="lg:col-span-2 bg-white/5 border border-white/10 p-6">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 size={20} className="text-[var(--primary)]" />
            <h3 className="text-white font-medium">Visitas por Día</h3>
          </div>
          
          <div className="space-y-2">
            {dashboard?.views_by_day?.slice(-14).map((day, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-white/50 text-xs w-20 flex-shrink-0">
                  {new Date(day.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                </span>
                <div className="flex-grow bg-white/5 h-6 relative">
                  <div 
                    className="absolute left-0 top-0 bottom-0 bg-gradient-to-r from-[var(--primary)] to-[var(--primary)]/50"
                    style={{ width: getBarWidth(day.views, maxDailyViews) }}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-white/70">
                    {day.views}
                  </span>
                </div>
              </div>
            )) || <p className="text-white/30">Sin datos</p>}
          </div>
        </div>

        {/* Devices Breakdown */}
        <div className="bg-white/5 border border-white/10 p-6">
          <div className="flex items-center gap-2 mb-6">
            <Monitor size={20} className="text-[var(--primary)]" />
            <h3 className="text-white font-medium">Dispositivos</h3>
          </div>
          
          <div className="space-y-4">
            {dashboard?.devices?.map((device, i) => {
              const total = dashboard.devices.reduce((sum, d) => sum + d.count, 0);
              const percentage = total > 0 ? Math.round((device.count / total) * 100) : 0;
              
              return (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2 text-white/70">
                      {getDeviceIcon(device.device)}
                      <span className="capitalize">{device.device}</span>
                    </div>
                    <span className="text-white font-medium">{percentage}%</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${
                        device.device === 'desktop' ? 'bg-blue-500' :
                        device.device === 'mobile' ? 'bg-green-500' : 'bg-purple-500'
                      }`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <p className="text-white/40 text-xs mt-1">{device.count.toLocaleString()} visitas</p>
                </div>
              );
            }) || <p className="text-white/30">Sin datos</p>}
          </div>
        </div>
      </div>

      {/* Bottom Row - Top Pages & Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Most Visited Pages */}
        <div className="bg-white/5 border border-white/10 p-6">
          <div className="flex items-center gap-2 mb-6">
            <FileText size={20} className="text-[var(--primary)]" />
            <h3 className="text-white font-medium">Páginas Más Visitadas</h3>
          </div>
          
          <div className="space-y-3">
            {dashboard?.top_pages?.map((page, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-[var(--primary)] font-bold w-6">{i + 1}</span>
                <div className="flex-grow">
                  <p className="text-white text-sm truncate">{page.title || page.page}</p>
                  <p className="text-white/40 text-xs truncate">{page.page}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-20 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[var(--primary)]"
                      style={{ width: getBarWidth(page.views, maxPageViews) }}
                    />
                  </div>
                  <span className="text-white/70 text-sm w-12 text-right">{page.views}</span>
                </div>
              </div>
            )) || <p className="text-white/30">Sin datos</p>}
          </div>
        </div>

        {/* Most Viewed Content */}
        <div className="bg-white/5 border border-white/10 p-6">
          <div className="flex items-center gap-2 mb-6">
            <Clock size={20} className="text-[var(--primary)]" />
            <h3 className="text-white font-medium">Contenidos Más Vistos</h3>
          </div>
          
          <div className="space-y-3">
            {dashboard?.top_content?.length > 0 ? dashboard.top_content.map((content, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 transition-colors">
                <span className="text-[var(--primary)] font-bold w-6">{i + 1}</span>
                <div className="flex-grow">
                  <p className="text-white text-sm">{content.title}</p>
                  <p className="text-white/40 text-xs">ID: {content.id?.slice(0, 8)}...</p>
                </div>
                <div className="text-right">
                  <p className="text-white font-medium">{content.views}</p>
                  <p className="text-white/40 text-xs">visitas</p>
                </div>
              </div>
            )) : (
              <p className="text-white/30 text-center py-8">
                Sin datos de eventos visitados.<br/>
                <span className="text-xs">Los datos aparecerán cuando los usuarios visiten las fichas de eventos.</span>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
